import {
  SiblingEventMutation,
  useLayout,
  useQueryState,
  useTranslations,
} from '@dustin-web/microsite-components/src/hooks'
import { getCategoryUrl } from '@dustin-web/microsite-components/src/utils/urls'
import classNames from 'classnames'
import Link from 'next/link'
import React, { useCallback, useEffect, useState } from 'react'
import { NavigationItemFragment } from '@productsite/src/graphql/generated/graphql-types'
import useCleanRouter from '@dustin-web/microsite-components/src/hooks/use-clean-router'
import ScrollContainer from '@dustin-web/microsite-components/src/scroll-container'
import Button from '@dustin-web/microsite-components/src/button'
import { FilterPageType } from './filter-page-layout'
import ExpandButton from '@dustin-web/microsite-components/src/expand-button'
// trigger change
type Props = {
  navigationItems: NavigationItemFragment[]
  showAll: boolean
  scrollTo?: boolean
  scrollToSiblingMutation?: SiblingEventMutation<string>
  accessoryCategories?: boolean
  pageType?: FilterPageType
  showOnlyUrlQuery?: string
  filterPageBaseUrl?: string
  activeAccessoryMenuItem?: string | null | undefined
  onItemClick?: (val: string) => void
  hideHits?: boolean
}

function formatCategoryName(string: string) {
  return string
    .replace(/[\s&]+/g, '_') // Replace spaces and ampersands with underscores
    .toLowerCase() // Convert to lowercase
}
const CategoryNavigationItemList = ({
  navigationItems,
  showAll = false,
  scrollTo = false,
  scrollToSiblingMutation,
  showOnlyUrlQuery,
  filterPageBaseUrl,
  accessoryCategories = false,
  activeAccessoryMenuItem,
  onItemClick,
  hideHits = false,
}: Props) => {
  const { responsive, header } = useLayout()
  const selectedNavItemIndex = navigationItems.findIndex(navItem => navItem.selected)
  const startFullyExpanded = selectedNavItemIndex >= 8
  const [fullyExpanded, setFullyExpanded] = useState(startFullyExpanded)

  const { translate } = useTranslations()
  const { query } = useCleanRouter()
  const { setQueryState } = useQueryState()
  const scrollToEl = useCallback(
    (navItem: NavigationItemFragment) => {
      if (!navItem.category?.name) {
        return false
      }
      if (scrollToSiblingMutation) {
        scrollToSiblingMutation(navItem.categoryId)
      }
    },
    [scrollToSiblingMutation]
  )

  useEffect(() => {
    const subTab = query.get('subTab')
    if (subTab === activeAccessoryMenuItem || !subTab) return

    const item = navigationItems.find(
      i => formatCategoryName(i.categoryId) === formatCategoryName(subTab)
    )

    if (item && scrollToSiblingMutation) {
      setQueryState({ subTab: '' }, false)
      scrollToEl(item)
    }
  }, [navigationItems, query, activeAccessoryMenuItem, scrollToEl, scrollToSiblingMutation])

  const getQueryString = (urlSearchParams: URLSearchParams) => {
    const queryStringKeyValue: string[] = []
    for (const keyValue of urlSearchParams.entries()) {
      queryStringKeyValue.push(`${keyValue[0]}=${keyValue[1]}`)
    }

    return queryStringKeyValue.join('&')
  }

  query?.delete('page')
  let queryWithQuestionMark = query?.toString() ? `?${getQueryString(query)}` : ''

  if (showOnlyUrlQuery) {
    const showonlyValues = query.getAll('showonly').filter(value => value !== showOnlyUrlQuery)
    const queries = showonlyValues.length > 0
      ? `showonly=${showOnlyUrlQuery},${showonlyValues.join(',')}`
      : `showonly=${showOnlyUrlQuery}`
    queryWithQuestionMark = `?${queries}`
  }

  const expandableBreakpoint = 8
  const expandable = navigationItems.length > expandableBreakpoint
  const compactList = navigationItems.length > 12
  const clickHandler = (navItem: NavigationItemFragment) => {
    scrollToEl(navItem)
    onItemClick &&
      navItem.category?.englishCategoryName &&
      onItemClick(navItem.category?.englishCategoryName)
  }
  if (accessoryCategories) {
    return (
      <div id="accessory-menu" className="u-mb-8">
        <ScrollContainer
          height={responsive.isSmallScreen ? 'none' : `calc(100vh - ${header.height.px + 100}px)`}
          gradientColor="light-gray">
          <ul
            className={classNames('ul-stripped u-slim-dustin-scrollbar u-overflow-auto  u-mb-0', {
              'u-fs-14': compactList,
            })}>
            {navigationItems
              .map((navItem, i) => (
                <li key={navItem.categoryId + i}>
                  <Button
                    defaultBtn
                    className={classNames(
                      'button-stripped u-w-100 u-mb-0 u-radius-4 justify-content-between u-decoration-none',
                      {
                        'u-bg-white':
                          activeAccessoryMenuItem === navItem.categoryId &&
                          !responsive.isSmallScreen,
                        'u-px-12': !responsive.isSmallScreen,
                        'u-px-0': responsive.isSmallScreen,
                        'u-py-8': !compactList,
                        'u-py-4': compactList,
                      }
                    )}
                    onClick={() => clickHandler(navItem)}
                    style={{ transition: `background-color 0.3s ease-in` }}
                    text={
                      <>
                        <div className="u-mr-8">
                          {navItem.category?.seoLongTitleName ?? navItem.category?.name}
                        </div>
                        {!hideHits && <span>{navItem.hits}</span>}
                      </>
                    }
                  />
                </li>
              ))
              .filter(
                (_item, i) => i < (fullyExpanded ? navigationItems.length : expandableBreakpoint)
              )}
          </ul>
        </ScrollContainer>
        {expandable && (
          <div
            className={classNames({
              'u-px-12': !responsive.isSmallScreen,
            })}>
            <Button
              defaultBtn
              id="siblings-collapsible-facets"
              className="button-stripped u-mb-0"
              style={{ position: 'relative', left: -5 }}
              icon={fullyExpanded ? 'arrow-up-small' : 'arrow-down-small'}
              text={<span>{translate(fullyExpanded ? 'CollapseFacets' : 'ExpandFacets')}</span>}
              onClick={e => {
                e.preventDefault()
                setFullyExpanded(() => !fullyExpanded)
              }}
            />
          </div>
        )}
      </div>
    )
  }

  return (
    <>
      <ul className="c-filter-categories ul-stripped">
        {navigationItems.map((navItem, i) => {
          const pathSlug = navItem.category?.pathSlug
          if (!pathSlug) {
            return null
          }
          const isFilterPageContext =
            filterPageBaseUrl && !filterPageBaseUrl.startsWith('/group')
          const categoryUrl = isFilterPageContext
            ? `${filterPageBaseUrl}/${pathSlug}`
            : getCategoryUrl(pathSlug)
          if (!categoryUrl) {
            return null
          }

          return (
            (i < 8 || fullyExpanded || navigationItems.length <= 9) && (
              <li key={i} className="c-filter-categories__category">
                {scrollTo ? (
                  <Button
                    defaultBtn
                    onClick={() => clickHandler(navItem)}
                    className={classNames('button-stripped', 'u-mb-0', {
                      'c-filter-categories__category--selected': navItem.selected,
                      'c-filter-categories__category-link': true,
                    })}
                    text={
                      <>
                        <span className="c-filter-categories__category-name">
                          {navItem.category?.seoLongTitleName ?? navItem.category?.name}
                        </span>
                        {!hideHits && <span>{navItem.hits}</span>}
                      </>
                    }
                  />
                ) : (
                  <Link
                    onClick={() => {
                      onItemClick &&
                        navItem.category?.englishCategoryName &&
                        onItemClick(navItem.category?.englishCategoryName)
                    }}
                    key={i}
                    prefetch={false}
                    shallow={false}
                    href={`${categoryUrl}${queryWithQuestionMark}`}
                    scroll={true}
                    className={classNames({
                      'c-filter-categories__category--selected': navItem.selected,
                      'c-filter-categories__category-link': true,
                    })}>
                    <span
                      className={classNames('c-filter-categories__category-name', {
                        decoration: responsive.isSmallScreen,
                      })}>
                      {navItem.category?.seoLongTitleName ?? navItem.category?.name}
                    </span>
                    {!hideHits && <span>{navItem.hits}</span>}
                  </Link>
                )}
              </li>
            )
          )
        })}
      </ul>

      {navigationItems.length > 9 && !showAll && (
        <div className="u-pt-16 d-flex">
          <ExpandButton
            collapsedTitle={translate('ExpandFacets')}
            expandedTitle={translate('CollapseFacets')}
            expanded={fullyExpanded}
            onClick={e => {
              e.preventDefault()
              setFullyExpanded(prevState => !prevState)
            }}
            ariaControls={''}
          />
        </div>
      )}
    </>
  )
}

export default CategoryNavigationItemList
